/**
// @ts-check
 * @typedef {import('@/types').Task} Task
 */

/**
 * Builds a hierarchical structure within a flat array of tasks.
 * Each task object in the returned array is enriched with a `children` property,
 * which is an array of its direct descendant tasks, sorted by `orderKey`.
 *
 * @param {Task[]} tasks - The flat array of task objects from Firestore.
 * @returns {Array<Task & {children: Task[]}>} - The original flat array of tasks, where each task is enriched with its correctly sorted children.
 */
export const buildTaskTree = (tasks) => {
  if (!tasks || tasks.length === 0) {
    return [];
  }

  const taskMap = new Map();
  // First, create a clone of each task with an initialized `children` array.
  // This ensures we are not mutating the original `allTasks` array from the context.
  const tasksWithChildren = tasks.map((task) => ({ ...task, children: [] }));

  // Then, map each new task object by its ID for quick lookups.
  tasksWithChildren.forEach((task) => {
    taskMap.set(task.id, task);
  });

  // link children to their parents.
  tasksWithChildren.forEach((task) => {
    if (task.parentId && taskMap.has(task.parentId)) {
      const parent = taskMap.get(task.parentId);
      // The check `!parent.children.some(child => child.id === task.id)` is a safeguard,
      // though with the current logic, it might be redundant.
      if (
        parent &&
        !parent.children.some(
          (/** @type {Task} */ child) => child.id === task.id,
        )
      ) {
        parent.children.push(task);
      }
    }
  });

  // Sort the children for each node based on their `orderKey`.
  tasksWithChildren.forEach((task) => {
    if (task.children.length > 1) {
      task.children.sort((/** @type {Task} */ a, /** @type {Task} */ b) => {
        if (a.orderKey < b.orderKey) return -1;
        if (a.orderKey > b.orderKey) return 1;
        return 0;
      });
    }
  });

  // Calculate duration rollups (bottom-up recursion)
  // We identify the root tasks to start the traversal.
  const rootTasks = tasksWithChildren.filter(
    (t) => !t.parentId || !taskMap.has(t.parentId),
  );

  /**
   * @param {Task & {children: Task[]}} task
   * @param {number} [depth]
   */
  const calculateRollup = (task, depth = 0) => {
    if (task.children && task.children.length > 0) {
      let rollupDuration = 0;
      for (const child of task.children) {
        calculateRollup(child, depth + 1); // Calculate sub-subtasks first
        if (!child.completed && child.status !== 'Backlog') {
          rollupDuration += child.duration || 0;
        }
      }
      task.duration = rollupDuration; // Overwrite parent duration with the calculated rollup
    }
  };

  rootTasks.forEach(calculateRollup);

  // The entire list, now with populated children, is returned.
  return tasksWithChildren;
};
